/**
 * Parc actif du navigateur — étape 4 du chantier droits-multi-parcs
 * (.scratch/droits-multi-parcs/spec.md, décision D6).
 *
 * Un seul parc à la fois pour tous les onglets, gardé dans le navigateur et
 * jamais écrit dans la fiche users/{uid} : le téléphone reste sur le parc de
 * rattachement. La copie locale du profil (localStorage `userData`, lue par
 * getZooId() et plus de 180 fichiers) reçoit ce parc, ainsi que le rôle et les
 * droits de ce parc lus dans le registre accessGrants/{uid}.
 *
 * Fonctions pures, sauf la lecture et l'écriture du parc mémorisé (stockage
 * injecté). La bascule elle-même (remise à zéro, rechargement) est dans
 * activeParkSwitch.js.
 */

import { isResearchGuestProfile } from "./researchGuest";
import { hasWelfareReferentRight } from "./welfareReferent";

export const ACTIVE_PARK_KEY = "akongo.activePark.v1";

const ROLES = ["user", "admin", "superAdmin", "pedago", "pedagoadmin"];
// `bea` : « Référent bien-être animal », droit du parc comme `vet` (functions/access/zooAccess.js).
const PERMISSION_KEYS = ["vet", "technical", "zimsPush", "bea"];
const INACTIVE_STATUSES = ["suspended", "deleted"];

const record = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
const nonEmpty = (value) => (typeof value === "string" && value.trim() ? value : null);

/** Parc mémorisé pour ce compte, ou null (autre compte, valeur illisible, stockage absent). */
export function readStoredActivePark(storage, uid) {
  try {
    const stored = JSON.parse(storage.getItem(ACTIVE_PARK_KEY) || "null");
    if (!record(stored) || stored.uid !== uid || !nonEmpty(stored.zooId)) return null;
    return { zooId: stored.zooId, zooName: nonEmpty(stored.zooName) };
  } catch (_error) {
    return null;
  }
}

export function writeStoredActivePark(storage, { uid, zooId, zooName = null, now = Date.now() }) {
  storage.setItem(ACTIVE_PARK_KEY, JSON.stringify({ uid, zooId, zooName: nonEmpty(zooName), at: now }));
}

export function clearStoredActivePark(storage) {
  storage.removeItem(ACTIVE_PARK_KEY);
}

const roleOf = (user) => {
  const raw = user?.userRight;
  if (record(raw)) return raw.value == null ? null : String(raw.value);
  return nonEmpty(raw);
};

export const isActiveAccount = (user) => !!user && user.isDeleted !== true && user.isArchived !== true
  && user.disabled !== true && !INACTIVE_STATUSES.includes(user.userStatus);

const profilePermissions = (user) => {
  const rights = record(user?.rights) ? user.rights : record(user?.userRights) ? user.userRights : {};
  return {
    vet: rights.vet === true,
    technical: rights.technical === true,
    zimsPush: user?.canPushToZims === true,
    bea: hasWelfareReferentRight(user),
  };
};

const emptyPermissions = () => Object.fromEntries(PERMISSION_KEYS.map((key) => [key, false]));

/** Registre lisible et cohérent (même contrôle de forme que le serveur). */
export function isValidGrant(grant) {
  if (!record(grant) || grant.schemaVersion !== 1 || !record(grant.zoos)) return false;
  const active = [];
  for (const [zooId, entry] of Object.entries(grant.zoos)) {
    if (!record(entry) || !["active", "revoked"].includes(entry.status) || !ROLES.includes(entry.role)) return false;
    if (entry.status === "active") active.push(zooId);
  }
  const listed = Array.isArray(grant.zooIds) ? [...grant.zooIds].sort() : null;
  return !!listed && JSON.stringify(listed) === JSON.stringify(active.sort());
}

/**
 * Décision d'admission, identique à functions/access/zooAccess.js
 * (resolveZooAccess) ; cas partagés : tests/access/cases/access-decisions.json.
 * `grantState` : "present" | "absent" | "unreadable".
 */
export function resolveParkAccess({ user, grant = null, grantState = "absent", zooId }) {
  const deny = (reason) => ({ admitted: false, staff: false, role: null, permissions: emptyPermissions(), reason });
  if (!nonEmpty(zooId)) return deny("parc non précisé");
  if (!isActiveAccount(user)) return deny("compte inactif");
  const staff = user.akongoStaff === true;
  if (grantState === "unreadable") return deny("registre illisible");
  if (grantState === "present") {
    if (!isValidGrant(grant)) return deny("registre invalide");
    const entry = grant.zoos[zooId];
    if (!entry || entry.status !== "active") return deny("parc absent du registre");
    return {
      admitted: true,
      staff,
      role: entry.role,
      roleLabel: entry.roleLabel ?? null,
      roleFlags: entry.roleFlags || {},
      permissions: { ...emptyPermissions(), ...(entry.permissions || {}) },
      source: "grant",
    };
  }
  if (staff) return { admitted: true, staff: true, role: roleOf(user), permissions: profilePermissions(user), source: "staff" };
  if (user.zooId !== zooId) return deny("parc différent de la fiche");
  return { admitted: true, staff: false, role: roleOf(user), permissions: profilePermissions(user), source: "legacy" };
}

/**
 * Décision de la personne connectée pour `zooId`, d'après sa fiche et son
 * registre relus (pas la copie locale du profil, dont le parc est celui du
 * navigateur). `get(ref)` : lecture d'un document, par exemple
 * `(ref) => tx.get(ref)` dans une transaction. Registre illisible : refus.
 */
export async function readOwnParkAccess({ firestore, uid, zooId, get = (ref) => ref.get() }) {
  const userSnap = await get(firestore.collection("users").doc(uid));
  const user = userSnap?.exists ? userSnap.data() : null;
  let grantRead;
  try {
    const grantSnap = await get(firestore.collection("accessGrants").doc(uid));
    grantRead = grantSnap?.exists ? { grant: grantSnap.data(), grantState: "present" } : { grant: null, grantState: "absent" };
  } catch (_error) {
    grantRead = { grant: null, grantState: "unreadable" };
  }
  return { user, ...resolveParkAccess({ user, ...grantRead, zooId }) };
}

/**
 * Parcs proposés par le sélecteur : les parcs actifs du registre. Un membre du
 * staff sans registre (avant le rattrapage) n'a pas de liste : il passe par
 * « Tous les parcs ».
 */
export function switchableParkIds({ user, grant = null, grantState = "absent" }) {
  if (!isActiveAccount(user) || grantState !== "present" || !isValidGrant(grant)) return [];
  return [...grant.zooIds].sort();
}

/**
 * Parc à ouvrir : le parc mémorisé s'il est encore autorisé, sinon le parc de
 * la fiche, sinon le parc de rattachement du registre, sinon le premier parc
 * actif. null si aucun n'est autorisé (compte sans accès).
 */
export function chooseActivePark({ user, grant = null, grantState = "absent", stored = null }) {
  // Invité recherche : aucun parc, même si le navigateur en a mémorisé un.
  if (isResearchGuestProfile(user) && user.akongoStaff !== true) return null;
  const candidates = [stored?.zooId, user?.zooId, grant?.homeZooId, ...(Array.isArray(grant?.zooIds) ? [...grant.zooIds].sort() : [])];
  for (const zooId of candidates) {
    if (nonEmpty(zooId) && resolveParkAccess({ user, grant, grantState, zooId }).admitted) return zooId;
  }
  return null;
}

/**
 * Copie locale du profil pour le parc actif. Parc de la fiche : la fiche est
 * déjà la copie de ce parc (tenue par le serveur), seul le libellé du parc peut
 * manquer. Autre parc d'un compte à registre : rôle, libellé, indicateurs et
 * droits de ce parc. Le staff garde son rôle AKONGO, porté par sa fiche.
 * `emailSubscription` et `rights.vetData` restent des données de profil.
 */
export function overlayProfileForPark(profile, { zooId, zooName = null, decision } = {}) {
  // Sans parc (invité recherche) : la copie reste la fiche, sans parc inventé.
  if (!nonEmpty(zooId)) return { ...(record(profile) ? profile : {}), activeZooId: null };
  const previousZoo = record(profile.userZoo) ? profile.userZoo : {};
  const label = nonEmpty(zooName) || (previousZoo.value === zooId ? previousZoo.label : null) || null;
  const next = {
    ...profile,
    zooId,
    userZoo: { ...previousZoo, value: zooId, ...(label ? { label } : {}) },
    activeZooId: zooId,
  };
  if (!label) delete next.userZoo.label;
  if (zooId === profile.zooId || !decision || decision.source !== "grant" || decision.staff) return next;

  const previousRight = record(profile.userRight) ? profile.userRight : {};
  const userRight = { ...previousRight, value: decision.role };
  if (decision.roleLabel != null) userRight.label = decision.roleLabel;
  else delete userRight.label;
  if (decision.roleFlags && decision.roleFlags.isBEA != null) userRight.isBEA = decision.roleFlags.isBEA;
  else delete userRight.isBEA;
  const previousRights = record(profile.rights) ? profile.rights : {};
  return {
    ...next,
    userRight,
    rights: {
      ...previousRights,
      vet: decision.permissions.vet === true,
      technical: decision.permissions.technical === true,
      bea: decision.permissions.bea === true,
    },
    canPushToZims: decision.permissions.zimsPush === true,
  };
}
